/**
 * Client-side contour generation from well points using IDW + marching squares.
 * Used for future prediction dates where no server-computed contours exist.
 */
import type { WellPoint, WaterTableContour, ContourLine } from '../../api/phreatic';

interface GridPoint {
  lat: number;
  lon: number;
  val: number;
}

function idwInterpolate(
  lat: number, lon: number,
  points: GridPoint[],
  power: number = 2,
): number {
  let wSum = 0;
  let vSum = 0;
  for (const p of points) {
    const d = Math.sqrt((lat - p.lat) ** 2 + (lon - p.lon) ** 2);
    if (d < 1e-8) return p.val;
    const w = 1 / d ** power;
    wSum += w;
    vSum += w * p.val;
  }
  return wSum > 0 ? vSum / wSum : 0;
}

/**
 * Marching squares: extract iso-lines at a given threshold from a 2D grid.
 * Returns line segments as [lat, lon] pairs.
 */
function marchingSquares(
  grid: number[][],
  rows: number, cols: number,
  threshold: number,
  latMin: number, latMax: number,
  lonMin: number, lonMax: number,
): number[][] {
  const dLat = (latMax - latMin) / (rows - 1);
  const dLon = (lonMax - lonMin) / (cols - 1);
  const coords: number[][] = [];

  for (let i = 0; i < rows - 1; i++) {
    for (let j = 0; j < cols - 1; j++) {
      const tl = grid[i][j];
      const tr = grid[i][j + 1];
      const bl = grid[i + 1][j];
      const br = grid[i + 1][j + 1];

      // Binary classification
      const code =
        (tl >= threshold ? 8 : 0) |
        (tr >= threshold ? 4 : 0) |
        (br >= threshold ? 2 : 0) |
        (bl >= threshold ? 1 : 0);

      if (code === 0 || code === 15) continue;

      const lat0 = latMin + i * dLat;
      const lat1 = latMin + (i + 1) * dLat;
      const lon0 = lonMin + j * dLon;
      const lon1 = lonMin + (j + 1) * dLon;

      // Interpolation helpers
      const lerp = (a: number, b: number, va: number, vb: number) => {
        const t = (threshold - va) / (vb - va || 1);
        return a + t * (b - a);
      };

      // Top edge interpolation
      const topLon = lerp(lon0, lon1, tl, tr);
      const rightLat = lerp(lat0, lat1, tr, br);
      const bottomLon = lerp(lon0, lon1, bl, br);
      const leftLat = lerp(lat0, lat1, tl, bl);

      // Cases produce line segments (simplified — single segment per cell)
      const segments: [number, number, number, number][] = [];
      switch (code) {
        case 1: case 14: segments.push([leftLat, lon0, lat1, bottomLon]); break;
        case 2: case 13: segments.push([lat1, bottomLon, rightLat, lon1]); break;
        case 3: case 12: segments.push([leftLat, lon0, rightLat, lon1]); break;
        case 4: case 11: segments.push([lat0, topLon, rightLat, lon1]); break;
        case 6: case 9: segments.push([lat0, topLon, lat1, bottomLon]); break;
        case 7: case 8: segments.push([lat0, topLon, leftLat, lon0]); break;
        case 5: // saddle
          segments.push([lat0, topLon, rightLat, lon1]);
          segments.push([leftLat, lon0, lat1, bottomLon]);
          break;
        case 10: // saddle
          segments.push([lat0, topLon, leftLat, lon0]);
          segments.push([lat1, bottomLon, rightLat, lon1]);
          break;
      }

      for (const [lat1s, lon1s, lat2s, lon2s] of segments) {
        coords.push([lat1s, lon1s]);
        coords.push([lat2s, lon2s]);
      }
    }
  }

  // Chain nearby segments into polylines
  if (coords.length < 2) return [];
  const lines: number[][] = [];
  for (let k = 0; k < coords.length; k += 2) {
    lines.push(coords[k], coords[k + 1]);
  }
  return lines;
}

/**
 * Generate contours from well points entirely on the client.
 */
export function generateClientContours(
  wells: WellPoint[],
  date: string,
  numLevels: number = 8,
  gridRes: number = 30,
): WaterTableContour | null {
  const valid = wells.filter(
    w => w.latitude && w.longitude && w.water_elevation_ft != null && w.water_elevation_ft > 50,
  );
  if (valid.length < 3) return null;

  const lats = valid.map(w => w.latitude);
  const lons = valid.map(w => w.longitude);
  const elevs = valid.map(w => w.water_elevation_ft!);

  const latMin = Math.min(...lats) - 0.005;
  const latMax = Math.max(...lats) + 0.005;
  const lonMin = Math.min(...lons) - 0.005;
  const lonMax = Math.max(...lons) + 0.005;

  const points: GridPoint[] = valid.map(w => ({
    lat: w.latitude,
    lon: w.longitude,
    val: w.water_elevation_ft!,
  }));

  // Build IDW grid
  const grid: number[][] = [];
  for (let i = 0; i < gridRes; i++) {
    const row: number[] = [];
    const lat = latMin + (i / (gridRes - 1)) * (latMax - latMin);
    for (let j = 0; j < gridRes; j++) {
      const lon = lonMin + (j / (gridRes - 1)) * (lonMax - lonMin);
      row.push(idwInterpolate(lat, lon, points));
    }
    grid.push(row);
  }

  // Determine contour levels
  const minElev = Math.min(...elevs);
  const maxElev = Math.max(...elevs);
  const range = maxElev - minElev;
  if (range < 1) return null;

  const step = range / (numLevels + 1);
  const levels: number[] = [];
  for (let k = 1; k <= numLevels; k++) {
    levels.push(Math.round(minElev + step * k));
  }

  // Extract contour lines
  const contours: ContourLine[] = [];
  for (const level of levels) {
    const coords = marchingSquares(grid, gridRes, gridRes, level, latMin, latMax, lonMin, lonMax);
    if (coords.length >= 2) {
      contours.push({ elevation_ft: level, coordinates: coords });
    }
  }

  // Compute flow direction (gradient from high to low)
  let flowDir: number | null = null;
  if (valid.length >= 3) {
    // Simple: direction from highest to lowest well
    const highest = valid.reduce((a, b) => (a.water_elevation_ft! > b.water_elevation_ft! ? a : b));
    const lowest = valid.reduce((a, b) => (a.water_elevation_ft! < b.water_elevation_ft! ? a : b));
    const dLat = lowest.latitude - highest.latitude;
    const dLon = lowest.longitude - highest.longitude;
    flowDir = (Math.atan2(dLon, dLat) * 180) / Math.PI;
    if (flowDir < 0) flowDir += 360;
  }

  return {
    site_id: '',
    gauge_date: date,
    contours,
    flow_direction_deg: flowDir,
    flow_magnitude: null,
    grid_bounds: { min_lat: latMin, max_lat: latMax, min_lon: lonMin, max_lon: lonMax },
  };
}
